// TermsAmdConditions.jsx

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Shield, FileText, Users, Lock, AlertCircle,
    Mail, Phone, Clock, ChevronRight, CheckCircle
} from 'lucide-react';
import Footer from '../components/Footer';

const TermsAmdConditions = () => {
    const [scrollY, setScrollY] = useState(0);
    const [activeSection, setActiveSection] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const handleScroll = () => {
            setScrollY(window.scrollY);

            const elements = document.querySelectorAll('.fade-in');
            elements.forEach((el, index) => {
                const rect = el.getBoundingClientRect();
                if (rect.top >= 0 && rect.top <= window.innerHeight * 0.8) {
                    setTimeout(() => {
                        el.classList.add('animate-in');
                    }, index * 100);
                }
            });
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const termsSections = [
        {
            id: 'introduction',
            title: 'Introduction',
            icon: Shield,
            content: [
                'By accessing or using Navam Biz Solutions\'s platform (https://www.navambizsolutions.com), you agree to these Terms and Conditions. Your continued use signifies acceptance of these terms.',
                'These terms govern your use of our websites, applications, and services provided by Navam Biz Solutions ("we," "us," or "our").'
            ]
        },
        {
            id: 'use-of-platform',
            title: 'Use of the Platform',
            icon: Users,
            content: ['To ensure a safe and productive environment, we have established rules for using our platform:'],
            list: [
                'Eligibility: Users must be 18 years or older to access our platform.',
                'Account Security: You are responsible for keeping your account credentials safe and accountable for all actions performed through your account.',
                'Prohibited Activities: Unlawful, harmful, or disruptive activities, including hacking, spamming, or distributing malicious content, are strictly prohibited.'
            ]
        },
        {
            id: 'user-content',
            title: 'User Content',
            icon: FileText,
            content: ['Content you submit to our platform remains your property, but you grant us specific rights to use it for operational purposes.'],
            list: [
                'You grant Navam Biz Solutions a non-exclusive, royalty-free, worldwide license to use, display, and distribute your content for service improvement and platform functionality.',
                'Ensure your content is accurate, lawful, and does not infringe upon third-party rights.',
                'We reserve the right to remove or modify content that violates these terms.'
            ]
        },
        {
            id: 'privacy',
            title: 'Privacy',
            icon: Lock,
            content: [
                'Your privacy is important to us. Our data practices are outlined in our Privacy Policy, which is incorporated into these terms by reference.'
            ],
            list: [
                'Please review our Privacy Policy at https://www.navambizsolutions.com/privacy-policy for details on how we collect, use, and protect your information.'
            ]
        },
        {
            id: 'payment-and-refunds',
            title: 'Payment Terms and Refund Policy',
            icon: FileText,
            content: [
                'All payments made through our platform are governed by the terms outlined below.'
            ],
            list: [
                'Users agree to pay all charges applicable to their use of our services.',
                'Fees once paid are non-refundable unless otherwise specified.',
                'Refunds (if applicable) are subject to evaluation and approval by our billing team.',
                'Late or failed payments may result in service suspension or termination.'
            ]
        },
        {
            id: 'intellectual-property',
            title: 'Intellectual Property',
            icon: Shield,
            content: ['All content, trademarks, logos, and software on our platform are protected by intellectual property laws.'],
            list: [
                'All materials, including but not limited to text, graphics, and code, are the property of Navam Biz Solutions or its licensors.',
                'Unauthorized use, reproduction, or distribution of our intellectual property is strictly prohibited.'
            ]
        },
        {
            id: 'third-party-services',
            title: 'Third-Party Services',
            icon: FileText,
            content: [
                'Our platform may contain links to third-party websites or services that are not owned or controlled by Navam Biz Solutions.'
            ],
            list: [
                'We are not responsible for the content, policies, or practices of third-party websites or services.',
                'Use of third-party tools is subject to their respective terms and privacy policies.'
            ]
        },
        {
            id: 'termination',
            title: 'Termination and Suspension',
            icon: AlertCircle,
            content: ['We reserve the right to suspend or terminate your access to the platform under certain conditions.'],
            list: [
                'Violation of any of these Terms and Conditions.',
                'Suspected fraudulent, abusive, or unlawful activity.',
                'Failure to comply with applicable legal obligations or payment commitments.',
                'We may terminate access with or without notice, and such termination may lead to forfeiture of associated data.'
            ]
        },
        {
            id: 'governing-law',
            title: 'Governing Law and Jurisdiction',
            icon: FileText,
            content: ['These Terms and Conditions shall be governed by and interpreted in accordance with the laws of India.'],
            list: [
                'Any disputes arising out of or in connection with the platform shall be subject to the exclusive jurisdiction of the courts of Pune, Maharashtra.',
                'Users agree to resolve any legal matters in this jurisdiction, and waive all rights to other legal venues unless required by law.'
            ]
        },
        {
            id: 'force-majeure',
            title: 'Force Majeure',
            icon: Clock,
            content: [
                'Navam Biz Solutions shall not be held liable for any failure or delay in performance due to events beyond our reasonable control.'
            ],
            list: [
                'This includes but is not limited to natural disasters, strikes, wars, government actions, or internet service disruptions.',
                'We will attempt to notify users of such occurrences when possible and resume services promptly.'
            ]
        },
        {
            id: 'limitation-of-liability',
            title: 'Limitation of Liability',
            icon: AlertCircle,
            content: ['Navam Biz Solutions strives to provide a reliable platform but cannot guarantee uninterrupted or error-free service.'],
            list: [
                'We will not be liable for any direct, indirect, incidental, or consequential damages arising from the use or inability to use our platform.',
                'Our services are provided "as is" without warranties of any kind, express or implied.'
            ]
        },
        {
            id: 'disclaimer',
            title: 'Disclaimers',
            icon: AlertCircle,
            content: [
                'While we strive to maintain the accuracy and functionality of our platform, we make no guarantees or warranties.'
            ],
            list: [
                'Navam Biz Solutions makes no representations regarding the accuracy or completeness of any content.',
                'All services are provided "as is" and "as available" without warranties of any kind.',
                'We do not guarantee that the platform will be uninterrupted, secure, or error-free.'
            ]
        },
        {
            id: 'updates-to-terms',
            title: 'Updates to Terms',
            icon: Clock,
            content: [
                'We may update these Terms and Conditions periodically to reflect changes in our practices or legal requirements.'
            ],
            list: [
                'Continued use of the platform after updates constitutes acceptance of the revised terms.',
                'Please review this page regularly for updates.'
            ]
        }
    ];

    const keyPoints = [
        {
            icon: Shield,
            title: 'User Responsibility',
            desc: 'You are responsible for your account security and content.'
        },
        {
            icon: Lock,
            title: 'Platform Security',
            desc: 'We protect our platform with industry-standard measures.'
        },
        {
            icon: CheckCircle,
            title: 'Your Rights',
            desc: 'You retain ownership of your content with limited usage rights granted to us.'
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50 relative overflow-hidden">
            {/* Floating Background Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-20 -left-20 w-96 h-96 bg-[#0e6d87]/5 rounded-full blur-3xl" style={{ transform: `translateY(${scrollY * 0.2}px)` }} />
                <div className="absolute bottom-20 -right-20 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" style={{ transform: `translateY(${scrollY * -0.3}px)` }} />
            </div>

            <section className="relative py-16 px-4 sm:px-6 lg:px-12">
                <div className="max-w-6xl mx-auto">
                    {/* Hero Section */}
                    <div className="text-center mb-16 fade-in">
                        <span className="inline-block mb-6 px-4 py-2 bg-[#0e6d87]/10 text-[#0e6d87] rounded-full text-sm font-medium">
                            Terms and Conditions
                        </span>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-gray-800 mb-6 leading-tight">
                            Terms and Conditions
                        </h1>
                        <p className="mt-6 max-w-3xl mx-auto text-lg text-gray-600 leading-relaxed">
                            Welcome to Navam Biz Solutions! Please read our terms carefully to understand your rights and responsibilities when using our platform.
                        </p>
                    </div>

                    {/* Key Points */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
                        {keyPoints.map((point, index) => {
                            const IconComponent = point.icon;
                            return (
                                <div key={index} className="bg-white rounded-lg shadow-md p-6 text-center fade-in">
                                    <div className="w-12 h-12 bg-[#0e6d87] rounded-lg flex items-center justify-center mx-auto mb-4">
                                        <IconComponent className="w-6 h-6 text-white" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-800 mb-2">{point.title}</h3>
                                    <p className="text-sm text-gray-600">{point.desc}</p>
                                </div>
                            );
                        })}
                    </div>

                    {/* Main Content */}
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                        {/* Table of Contents */}
                        <div className="lg:col-span-1">
                            <div className="bg-white rounded-lg shadow-md p-6 sticky top-8 fade-in">
                                <h3 className="text-lg font-semibold text-gray-800 mb-4">Contents</h3>
                                <nav className="space-y-2">
                                    {termsSections.map((section, index) => {
                                        const IconComponent = section.icon;
                                        return (
                                            <a
                                                key={index}
                                                href={`#${section.id}`}
                                                className={`flex items-center text-sm p-2 rounded-lg transition-colors duration-200 ${activeSection === section.id
                                                    ? 'bg-[#0e6d87] text-white'
                                                    : 'text-gray-600 hover:bg-gray-50'
                                                    }`}
                                                onClick={() => setActiveSection(section.id)}
                                            >
                                                <IconComponent className="w-4 h-4 mr-2 flex-shrink-0" />
                                                <span className="truncate">{section.title}</span>
                                            </a>
                                        );
                                    })}
                                </nav>
                            </div>
                        </div>

                        {/* Terms Content */}
                        <div className="lg:col-span-3 space-y-8">
                            {termsSections.map((section, index) => {
                                const IconComponent = section.icon;
                                return (
                                    <div key={index} id={section.id} className="bg-white rounded-lg shadow-md p-6 lg:p-8 fade-in">
                                        <div className="flex items-center mb-6">
                                            <div className="w-10 h-10 bg-[#0e6d87] rounded-lg flex items-center justify-center mr-4">
                                                <IconComponent className="w-5 h-5 text-white" />
                                            </div>
                                            <h2 className="text-2xl font-semibold text-gray-800">
                                                {index + 1}. {section.title}
                                            </h2>
                                        </div>

                                        <div className="space-y-4">
                                            {section.content.map((paragraph, pIndex) => (
                                                <p key={pIndex} className="text-gray-600 leading-relaxed">
                                                    {paragraph}
                                                </p>
                                            ))}
                                            {section.list && (
                                                <ul className="space-y-3 mt-4">
                                                    {section.list.map((item, lIndex) => (
                                                        <li key={lIndex} className="flex items-start">
                                                            <ChevronRight className="w-4 h-4 text-[#0e6d87] mr-2 mt-0.5 flex-shrink-0" />
                                                            <span className="text-gray-600">{item}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Footer Section */}
                    <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div className="bg-gray-100 rounded-lg p-6 fade-in">
                            <div className="flex items-center mb-4">
                                <Clock className="w-5 h-5 text-[#0e6d87] mr-2" />
                                <h3 className="font-semibold text-gray-800">Last Updated</h3>
                            </div>
                            <p className="text-gray-600 text-sm">
                                These Terms and Conditions were last updated on <strong>December 2024</strong>.
                            </p>
                        </div>

                        <div className="bg-white rounded-lg shadow-md p-6 fade-in">
                            <div className="flex items-center mb-4">
                                <Mail className="w-5 h-5 text-[#0e6d87] mr-2" />
                                <h3 className="font-semibold text-gray-800">Questions?</h3>
                            </div>
                            <p className="text-gray-600 text-sm mb-4">
                                If you have any questions about these Terms and Conditions, please contact us:
                            </p>
                            <div className="space-y-2 text-sm">
                                <div className="flex items-center text-gray-600">
                                    <Mail className="w-4 h-4 text-[#0e6d87] mr-2" />
                                    <a href="mailto:info@navambizsolutions.com" className="text-[#0e6d87] hover:underline">info@navambizsolutions.com</a>
                                </div>
                                <div className="flex items-center text-gray-600">
                                    <Phone className="w-4 h-4 text-[#0e6d87] mr-2" />
                                    <a href="tel:+917276156055" className="text-[#0e6d87] hover:underline">+91 72761 56055</a>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CTA */}
                    <div className="mt-16 text-center bg-gray-100 rounded-lg p-8 fade-in">
                        <h2 className="text-2xl font-semibold text-gray-800 mb-4">Questions About Our Terms?</h2>
                        <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                            We're here to help. Contact our team for any questions or concerns about our Terms and Conditions.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={() => navigate('/contact')}
                                className="inline-flex items-center px-5 py-2.5 bg-[#0e6d87] text-white font-semibold rounded-lg shadow hover:shadow-md hover:bg-[#0b566a] transition-all duration-300"
                            >
                                <Mail className="w-4 h-4 mr-2" />
                                Contact Us
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />

            <style jsx>{`
        .fade-in {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        .animate-in {
          opacity: 1;
          transform: translateY(0);
        }
        html {
          scroll-behavior: smooth;
        }
      `}</style>
        </div>
    );
};

export default TermsAmdConditions;
